import { useSnackbar } from 'notistack'
import * as React from 'react'
import { DOCUMENT_DEFAULT_WHERE_ARGS } from '../../components/GravityForms/Fields/searchers/DocumentSearchField'
import { OFFLINE_DATA_BATCH_SIZE } from '../../constants'
import {
  RootQueryToDocumentConnectionEdge,
  useAllDocumentsLazyQuery,
} from '../../types/generated'
import { UseOfflineDataReturnValue } from '../../types/offlineData'
import usePrevious from '../usePrevious'

const useOfflineDocuments = (
  setOfflineData: React.AsyncDispatch<
    React.SetStateAction<RootQueryToDocumentConnectionEdge[]>
  >
): UseOfflineDataReturnValue => {
  const { enqueueSnackbar } = useSnackbar()
  const batchSize = OFFLINE_DATA_BATCH_SIZE
  const [fetching, setFetching] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const startLoading = () => setIsLoading(true)
  const stopLoading = () => setIsLoading(false)

  const [getDocuments, { refetch, called, fetchMore, loading, data }] =
    useAllDocumentsLazyQuery({
      variables: {
        first: batchSize,
        after: null,
        where: DOCUMENT_DEFAULT_WHERE_ARGS,
      },
      notifyOnNetworkStatusChange: true,
    })

  const shouldFetchMore =
    (called && !loading && data?.documents?.pageInfo?.hasNextPage) ?? false

  if (shouldFetchMore && fetchMore) {
    fetchMore({
      variables: {
        first: batchSize,
        after: data?.documents?.pageInfo?.endCursor,
      },
    })
  }

  const doneFetching =
    ((!loading && called && data && !data?.documents?.pageInfo?.hasNextPage) ||
      data?.documents?.edges?.length === 0) ??
    false
  const prevDoneFetching = usePrevious(
    doneFetching && data?.documents?.pageInfo?.hasPreviousPage
  )

  React.useEffect(() => {
    if (doneFetching && !prevDoneFetching && fetching) {
      const edges = data?.documents?.edges ?? []
      enqueueSnackbar(`${edges?.length} Documents downloaded`)
      setTimeout(() => {
        // @ts-expect-error this is fine
        setOfflineData(edges).then(stopLoading).catch(stopLoading)
        setFetching(false)
      }, 300)
    }
    // eslint-disable-next-line
  }, [prevDoneFetching, doneFetching, enqueueSnackbar, fetching])

  return {
    getOfflineData: () => {
      setFetching(true)
      startLoading()
      !called ? getDocuments() : refetch?.()
    },
    loading: isLoading,
    done: doneFetching,
    called: called,
  }
}

export default useOfflineDocuments
